import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  Container,
  IconButton,
  InputAdornment,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Button,
  Tabs,
  Tab,
  Select,
  MenuItem,
  TextField,
  ThemeProvider,
  Tooltip,
  useMediaQuery,
  Typography,
  createTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BlockIcon from "@mui/icons-material/Block";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import useAccess from "../../hooks/useAccess";
import Swal from "sweetalert2";
import { useSnackbar } from "notistack";
import {
  downloadAccessRoster,
  useAccessUsers,
  useAllAccessUsers,
  useBulkStatus,
} from "../../hooks/useAccessManagement";
import ManageAccessDialog from "./ManageAccessDialog";
import NewUserDrawer from "./NewUserDrawer";
import ManagedByCell from "./ManagedByCell";
import ManagesCell from "./ManagesCell";
import BalanceCell from "./BalanceCell";
import ForceLogoutCell from "./ForceLogoutCell";
import StatusCell from "./StatusCell";
import { ROLE_META } from "./roleMeta";
import { avatarSrc, initialsOf, stringToColor } from "./avatarColor";
import { PRIMARY_BTN_SX, SECONDARY_BTN_SX } from "./styles";

// Page shell tokens shared with the rest of the app's feature screens — same gradient
// canvas, Quicksand type scale, 108px sidebar gutter and fade-in as
// components/reviewsLeaderboard/ReviewsLeaderboardPage.jsx, components/plans/Plans.jsx
// and components/whatsapp/*Page.jsx.
const theme = createTheme({
  typography: { fontFamily: ["Quicksand", "Inter", "sans-serif"].join(","), fontSize: 16 },
});

const PAGE_BG = {
  minHeight: "100vh",
  background: "linear-gradient(145deg, #EDF1FF 0%, #F2EEFF 38%, #FFF0ED 100%)",
  // `fixed` is janky-to-broken on iOS Safari, where it also forces a repaint on every
  // scroll frame — the gradient is full-height anyway, so phones just scroll it.
  backgroundAttachment: { xs: "scroll", md: "fixed" },
};

const CONTAINER_SX = {
  // Clears the fixed DashboardHeader, whose MUI Toolbar is 56px below 600px and 64px above.
  mt: { xs: "56px", sm: "64px" },
  pt: 3,
  pb: 6,
  pl: { xs: 2, sm: 3, lg: "108px" },
  pr: { xs: 2, sm: 3 },
  // A single stray wide child (the table, before it switches to cards) would otherwise give
  // the whole page a horizontal scrollbar and shrink nothing — this keeps that contained to
  // the element that caused it. Deliberately on the Container, not the PAGE_BG Box above:
  // that Box also hosts DashboardHeaderSection's `position: fixed` header as a sibling, and
  // an `overflow` other than `visible` on a fixed element's ancestor is exactly what makes
  // older iOS Safari re-anchor it to that ancestor instead of the viewport.
  overflowX: "hidden",
  "@keyframes pageFadeIn": {
    from: { opacity: 0, transform: "translateY(8px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
  animation: "pageFadeIn 0.36s ease both",
};

// Same table type/rhythm as components/reviewsLeaderboard/LeaderboardTable.jsx.
const HEAD_SX = {
  fontFamily: "Quicksand",
  fontWeight: 700,
  fontSize: 10,
  color: "#9CA3AF",
  textTransform: "uppercase",
  letterSpacing: 0.8,
  bgcolor: "#FAFAFA",
  borderBottom: "1px solid rgba(0,0,0,0.06)",
  borderRight: "1px solid rgba(0,0,0,0.06)",
  py: 1.5,
  px: 2,
  // Headers must never wrap — "Force logout" was breaking onto two lines and pushing the
  // header row taller than the data rows.
  whiteSpace: "nowrap",
};

const CELL_SX = {
  fontFamily: "Quicksand",
  fontWeight: 600,
  fontSize: 13,
  color: "#374151",
  borderBottom: "1px solid rgba(0,0,0,0.04)",
  borderRight: "1px solid rgba(0,0,0,0.04)",
  py: 1.5,
  px: 2,
};

// Sort arrow shares the header's own muted styling, and only darkens on the active
// column so an unsorted table doesn't look like it has a sort applied.
const SORT_LABEL_SX = {
  color: "inherit !important",
  "& .MuiTableSortLabel-icon": { fontSize: 15, opacity: 0.35 },
  "&.Mui-active .MuiTableSortLabel-icon": { color: "#FF5200 !important", opacity: 1 },
};

// Last column in a row — drops the separator so the table doesn't get an outer edge line.
const LAST_CELL_SX = { borderRight: "none" };

// Explicit widths, since auto-layout gave "Managed by" half the table while Balance and
// Status stayed cramped. TABLE_MIN_WIDTH (computed below, not hand-totaled — a hardcoded
// sum here drifted out of sync with these values once already) is below that it scrolls
// horizontally rather than squeezing columns into unreadable slivers.
const COL_WIDTH = {
  select: 52,
  serial: 56,
  user: 250,
  role: 120,
  balance: 185,
  managedBy: 210,
  manages: 170,
  status: 110,
  lastLogin: 160,
  forceLogout: 110,
  actions: 50,
};
const TABLE_MIN_WIDTH = Object.values(COL_WIDTH).reduce((sum, w) => sum + w, 0);
// + the 108px sidebar gutter (CONTAINER_SX's `lg` padding-left) — the real width the table
// needs before it stops scrolling sideways, derived from COL_WIDTH itself so the compact/
// card breakpoint below can never drift out of sync with it again.
const TABLE_BREAKPOINT_PX = TABLE_MIN_WIDTH + 108;

const PAGE_SIZE_OPTIONS = [20, 50, 100]; // backend caps `limit` at 200

// Quicksand everywhere, including the rows-per-page select and the arrows — MUI styles
// those from the default theme, so only the two label classes were picking up the page's
// font before and the control looked like it belonged to a different screen.
const PAGINATION_SX = {
  borderTop: "1px solid rgba(0,0,0,0.06)",
  "& .MuiTablePagination-toolbar": { minHeight: 52, px: { xs: 1, sm: 2 }, flexWrap: "wrap" },
  // "Rows per page" is the first thing to go on a phone — the number next to it still says
  // what the control does, and dropping the words keeps the whole bar on one line.
  "& .MuiTablePagination-selectLabel": { display: { xs: "none", sm: "block" } },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    fontFamily: "Quicksand",
    fontWeight: 700,
    fontSize: 12,
    color: "#6B7280",
  },
  "& .MuiTablePagination-select": {
    fontFamily: "Quicksand",
    fontWeight: 700,
    fontSize: 12,
    color: "#1C1C2E",
    borderRadius: "8px",
    bgcolor: "#F3F4F6",
    py: 0.5,
    pl: 1,
  },
  "& .MuiTablePagination-selectIcon": { color: "#9CA3AF" },
  "& .MuiTablePagination-actions": {
    "& .MuiIconButton-root": {
      color: "#6B7280",
      "&:hover": { bgcolor: "rgba(255,82,0,0.08)", color: "#FF5200" },
      "&.Mui-disabled": { color: "#D1D5DB" },
    },
  },
};

// The dropdown itself renders in a portal, so it needs its own styling to match.
const PAGINATION_MENU_SX = {
  borderRadius: 2,
  border: "1px solid rgba(0,0,0,0.06)",
  boxShadow: "0 8px 28px rgba(0,0,0,0.10)",
  "& .MuiMenuItem-root": {
    fontFamily: "Quicksand",
    fontWeight: 700,
    fontSize: 12,
    color: "#374151",
    "&.Mui-selected": { bgcolor: "rgba(255,82,0,0.10)", color: "#FF5200" },
    "&.Mui-selected:hover": { bgcolor: "rgba(255,82,0,0.14)" },
  },
};
const ROLE_TABS = [
  { value: "all", label: "All" },
  { value: "superadmin", label: ROLE_META.superadmin.label },
  { value: "admin", label: ROLE_META.admin.label },
  { value: "user", label: ROLE_META.user.label },
];

// Enabled/disabled filter. "All" is first so the default reads as a starting point rather
// than a filter someone forgot to clear — matching the role tabs above it.
const STATUS_FILTERS = [
  { value: "all", label: "All", icon: GroupsOutlinedIcon, color: "#6B7280" },
  { value: "active", label: "Active", icon: CheckCircleOutlineIcon, color: "#09AC31" },
  { value: "disabled", label: "Disabled", icon: BlockIcon, color: "#E34C14" },
];

// Same three sortable columns the table's TableSortLabel offers — the card list has no
// header row to hang those onto, so it gets this chip row instead rather than losing
// sorting entirely below the table breakpoint.
const SORT_OPTIONS = [
  { by: "name", label: "Name" },
  { by: "balance", label: "Balance" },
  { by: "lastLogin", label: "Last login" },
];

function RoleChip({ role }) {
  const meta = ROLE_META[role] || ROLE_META.user;
  return (
    <Chip
      size="small"
      label={meta.label}
      sx={{
        bgcolor: meta.bg,
        color: meta.color,
        fontFamily: "Quicksand",
        fontWeight: 700,
        fontSize: 11,
        letterSpacing: 0.2,
      }}
    />
  );
}

// "9/11/2026, 7:25 AM" — date as before, plus the time pinned to en-US regardless of the
// browser's own locale, so it never renders as "7:25 am" (lowercase) or 24h under an
// en-IN/en-GB system locale.
function formatLastLogin(dateValue) {
  if (!dateValue) return "—";
  const date = new Date(dateValue);
  const day = date.toLocaleDateString();
  const time = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  return `${day}, ${time}`;
}

function TableRowsSkeleton({ rows = 6 }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <TableRow key={i}>
          <TableCell sx={CELL_SX} padding="checkbox">
            <Skeleton variant="rounded" width={18} height={18} sx={{ mx: "auto" }} />
          </TableCell>
          <TableCell sx={CELL_SX} align="center">
            <Skeleton variant="text" width={18} sx={{ mx: "auto" }} />
          </TableCell>
          <TableCell sx={CELL_SX}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Skeleton variant="circular" width={32} height={32} />
              <Box sx={{ width: "60%" }}>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="90%" />
              </Box>
            </Stack>
          </TableCell>
          <TableCell sx={CELL_SX}><Skeleton variant="rounded" width={90} height={24} /></TableCell>
          <TableCell sx={CELL_SX}><Skeleton variant="rounded" width={100} height={24} /></TableCell>
          <TableCell sx={CELL_SX}><Skeleton variant="rounded" width={100} height={24} /></TableCell>
          <TableCell sx={CELL_SX}><Skeleton variant="rounded" width={70} height={24} /></TableCell>
          <TableCell sx={CELL_SX}><Skeleton variant="rounded" width={90} height={24} /></TableCell>
          <TableCell sx={CELL_SX}><Skeleton variant="text" width={140} /></TableCell>
          <TableCell sx={CELL_SX} align="center">
            <Skeleton variant="circular" width={20} height={20} sx={{ mx: "auto" }} />
          </TableCell>
          <TableCell sx={{ ...CELL_SX, ...LAST_CELL_SX }} align="right">
            <Skeleton variant="circular" width={24} height={24} sx={{ ml: "auto" }} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

// ── Compact layout ───────────────────────────────────────────────────────────────────
// The table is 1330px wide by design — eleven columns that each need their width to stay
// readable — so below `md` it's replaced by one card per user rather than left to scroll
// sideways. Same data, same actions, stacked: identity and the two chips up top, the
// detail cells as labelled pairs underneath.
const FIELD_LABEL_SX = {
  fontFamily: "Quicksand",
  fontWeight: 700,
  fontSize: 9.5,
  color: "#9CA3AF",
  textTransform: "uppercase",
  letterSpacing: 0.8,
  mb: 0.5,
};

function CardField({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={FIELD_LABEL_SX}>{label}</Typography>
      {children}
    </Box>
  );
}

function UserCard({ user, index, selected, onToggle, onOpen }) {
  return (
    <Box
      onClick={onOpen}
      sx={{
        p: 1.75,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: selected ? "rgba(255,82,0,0.35)" : "rgba(0,0,0,0.07)",
        bgcolor: selected ? "rgba(255,82,0,0.04)" : "#fff",
        cursor: "pointer",
        transition: "border-color 0.15s ease, background-color 0.15s ease",
      }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.25}>
        <Box onClick={(e) => e.stopPropagation()} sx={{ mt: -0.5, ml: -0.75 }}>
          <Checkbox
            size="small"
            checked={selected}
            onChange={onToggle}
            sx={{ "&.Mui-checked": { color: "#FF5200" } }}
          />
        </Box>
        <Avatar
          src={avatarSrc(user.image)}
          imgProps={{ referrerPolicy: "no-referrer" }}
          sx={{
            width: 36,
            height: 36,
            fontSize: 13,
            fontWeight: 800,
            fontFamily: "Quicksand",
            bgcolor: stringToColor(user.fullName),
          }}
        >
          {initialsOf(user.fullName)}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontFamily: "Quicksand", fontWeight: 700, fontSize: 13.5, color: "#1C1C2E", lineHeight: 1.3 }}
            noWrap
          >
            {index}. {user.fullName}
          </Typography>
          <Typography sx={{ fontFamily: "Quicksand", fontWeight: 600, fontSize: 11, color: "#9CA3AF" }} noWrap>
            {user.email}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.75, mt: 0.85 }}>
            <RoleChip role={user.role} />
            <Box onClick={(e) => e.stopPropagation()}>
              <StatusCell user={user} />
            </Box>
          </Box>
        </Box>
        <ChevronRightIcon sx={{ fontSize: 20, color: "#C4C8CE", flexShrink: 0 }} />
      </Stack>

      <Box
        sx={{
          mt: 1.5,
          pt: 1.5,
          borderTop: "1px dashed rgba(0,0,0,0.08)",
          display: "grid",
          // BalanceCell alone is ~110px of stepper, so each column needs ~140px. auto-fit
          // then decides the count from the card's real width rather than from a viewport
          // breakpoint, which is what matters when the card is inside varying gutters.
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 1.5,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <CardField label="Balance">
          <BalanceCell user={user} />
        </CardField>
        <CardField label="Last login">
          <Typography sx={{ fontFamily: "Quicksand", fontWeight: 600, fontSize: 11.5, color: "#6B7280" }}>
            {formatLastLogin(user.lastLogin)}
          </Typography>
        </CardField>
        <CardField label="Managed by">
          <ManagedByCell user={user} />
        </CardField>
        <CardField label="Manages">
          <ManagesCell user={user} />
        </CardField>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.25 }} onClick={(e) => e.stopPropagation()}>
        <Typography sx={FIELD_LABEL_SX}>Force logout</Typography>
        <ForceLogoutCell user={user} />
      </Stack>
    </Box>
  );
}

function CardsSkeleton({ rows = 4 }) {
  return (
    <Stack spacing={1.25}>
      {Array.from({ length: rows }).map((_, i) => (
        <Box key={i} sx={{ p: 1.75, borderRadius: 2.5, border: "1px solid rgba(0,0,0,0.07)" }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Skeleton variant="circular" width={36} height={36} />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton variant="text" width="55%" />
              <Skeleton variant="text" width="75%" />
            </Box>
          </Stack>
          <Skeleton variant="rounded" height={44} sx={{ mt: 1.5 }} />
        </Box>
      ))}
    </Stack>
  );
}

export default function AccessManagementPage() {
  const navigate = useNavigate();
  const { isSuperUser } = useAccess();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE_OPTIONS[0]);
  const [sort, setSort] = useState({ by: null, dir: "desc" }); // null = backend default order
  const [dormantOnly, setDormantOnly] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selected, setSelected] = useState([]); // ids checked in the table
  const [selectAllMatching, setSelectAllMatching] = useState(false); // beyond this page
  const bulkStatus = useBulkStatus();
  const [activeUser, setActiveUser] = useState(null);
  const [newUserOpen, setNewUserOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  // Below TABLE_BREAKPOINT_PX (the table's real minWidth plus the sidebar gutter — not a
  // standard MUI breakpoint, since that width falls between `lg` and `xl`) the same rows
  // render as cards instead (see UserCard) rather than the table just scrolling sideways.
  const isCompact = useMediaQuery(`(max-width:${TABLE_BREAKPOINT_PX}px)`);

  useEffect(() => {
    document.title = "Access Management | Meesha";
  }, []);

  // Real enforcement is server-side (routes/accessManagement.js requires superAdmin on
  // every endpoint) — this just keeps a non-Super-Admin who lands here from seeing a
  // half-loaded, all-403 page.
  useEffect(() => {
    if (!isSuperUser) navigate("/");
  }, [isSuperUser, navigate]);

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  // Any filter change re-starts from page 1 — staying on, say, page 3 of an unfiltered
  // list while switching to a 2-person role would just show an empty page.
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, roleFilter, statusFilter, dormantOnly, sort]);

  // A selection made under one filter shouldn't survive into another — the ids would no
  // longer match what's on screen, and a bulk action would hit invisible rows. Paging or
  // changing rows-per-page under the *same* filter doesn't invalidate anything selected, so
  // those deliberately aren't in this list — otherwise every pagination click would silently
  // wipe a multi-page selection (and cancel "Select all N matching this filter") mid-review.
  useEffect(() => {
    setSelected([]);
    setSelectAllMatching(false);
  }, [debouncedSearch, roleFilter, statusFilter, dormantOnly]);

  const listParams = {
    q: debouncedSearch,
    role: roleFilter,
    status: statusFilter,
    page: page + 1,
    limit: rowsPerPage,
    sortBy: sort.by,
    sortDir: sort.dir,
    dormant: dormantOnly,
  };
  const { data, isLoading, isFetching, isError } = useAccessUsers(listParams);
  const users = data?.data || [];
  const total = data?.total || 0;
  const roleCounts = data?.roleCounts || { superadmin: 0, admin: 0, user: 0 };
  const totalPeople = roleCounts.superadmin + roleCounts.admin + roleCounts.user;
  const statusCounts = data?.statusCounts || { all: 0, active: 0, disabled: 0 };
  const dormantCount = data?.dormantCount ?? 0;
  const dormantDays = data?.dormantDays ?? 90;

  // Clicking the same column flips direction; a new column starts descending, which is the
  // useful default for both "who has the most credits" and "who logged in most recently".
  const toggleSort = (by) =>
    setSort((prev) => ({ by, dir: prev.by === by && prev.dir === "desc" ? "asc" : "desc" }));

  const pageIds = users.map((u) => u.id);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));
  const someOnPageSelected = pageIds.some((id) => selected.includes(id)) && !allOnPageSelected;
  const selectionCount = selectAllMatching ? total : selected.length;

  const toggleRow = (id) => {
    setSelectAllMatching(false);
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const togglePage = () => {
    setSelectAllMatching(false);
    setSelected(allOnPageSelected ? [] : pageIds);
  };

  const clearSelection = () => {
    setSelected([]);
    setSelectAllMatching(false);
  };

  const handleBulkStatus = async (isActive) => {
    const verb = isActive ? "Enable" : "Disable";
    const result = await Swal.fire({
      title: `${verb} ${selectionCount} ${selectionCount === 1 ? "user" : "users"}?`,
      html: `<div style="font-size: 14px; font-family: 'Quicksand'">
          ${isActive
            ? "They'll be able to sign in again."
            : "They'll be signed out and blocked from signing in until re-enabled."}
          <br/><br/>
          <i>You and any Super Admins in the selection are skipped automatically.</i>
        </div>`,
      icon: isActive ? "question" : "warning",
      showCancelButton: true,
      confirmButtonText: `Yes, ${verb.toLowerCase()}`,
      cancelButtonText: "Cancel",
      confirmButtonColor: isActive ? "#09AC31" : "#E34C14",
      cancelButtonColor: "#6b7280",
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;

    try {
      const response = await bulkStatus.mutateAsync({
        isActive,
        ...(selectAllMatching
          ? {
              applyToFilter: true,
              q: debouncedSearch,
              role: roleFilter,
              status: statusFilter,
              dormant: dormantOnly,
            }
          : { userIds: selected }),
      });

      if (response?.success === false) {
        enqueueSnackbar(response.message, { variant: "error" });
        return;
      }

      const skipped = (response.skipped?.self || 0) + (response.skipped?.superAdmins || 0);
      enqueueSnackbar(
        `${response.modified} ${isActive ? "enabled" : "disabled"}${skipped ? ` · ${skipped} skipped` : ""}`,
        { variant: "success" }
      );
      clearSelection();
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.message || `Could not ${verb.toLowerCase()} users`, { variant: "error" });
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { rows } = await downloadAccessRoster(listParams);
      enqueueSnackbar(`Exported ${rows} users`, { variant: "success" });
    } catch (error) {
      enqueueSnackbar("Could not export the roster", { variant: "error" });
    } finally {
      setExporting(false);
    }
  };

  // Unfiltered roster for the hierarchy pickers in the Manage dialog and the New user
  // drawer — fetched only once one of them is actually open, not on every page load.
  const { data: allUsersData } = useAllAccessUsers(Boolean(activeUser) || newUserOpen);
  const allUsers = allUsersData?.data || [];

  const showSkeleton = isLoading && users.length === 0;

  const emptyMessage = useMemo(() => {
    if (debouncedSearch) return `No users match "${debouncedSearch}"`;
    if (dormantOnly) return `Nobody has been inactive for ${dormantDays}+ days`;
    if (statusFilter === "disabled") return "No disabled accounts — everyone can sign in";
    if (statusFilter === "active") return "No active accounts in this view";
    if (roleFilter !== "all") return `No ${ROLE_META[roleFilter]?.label.toLowerCase()}s yet`;
    return "No users found";
  }, [debouncedSearch, roleFilter, statusFilter, dormantOnly, dormantDays]);

  if (!isSuperUser) return null;

  return (
    <Box sx={PAGE_BG}>
      <DashboardHeaderSection hideSearch />
      <ThemeProvider theme={theme}>
        <Container sx={CONTAINER_SX} maxWidth="xl">
          {/* ── Header ── */}
          <Box sx={{ mb: 3.5 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.6,
                bgcolor: "rgba(255,82,0,0.08)",
                border: "1px solid rgba(255,82,0,0.15)",
                borderRadius: "20px",
                px: 1.25,
                py: 0.35,
                mb: 1,
              }}
            >
              <ShieldOutlinedIcon sx={{ fontSize: 12, color: "#FF5200" }} />
              <Typography
                sx={{
                  fontFamily: "Quicksand",
                  fontWeight: 800,
                  fontSize: 10,
                  color: "#FF5200",
                  textTransform: "uppercase",
                  letterSpacing: 1.2,
                }}
              >
                Admin
              </Typography>
            </Box>

            <Typography
              sx={{
                fontFamily: "Quicksand",
                fontWeight: 900,
                fontSize: { xs: 28, sm: 34, md: 38 },
                lineHeight: 1.05,
                mb: 0.75,
                background: "linear-gradient(120deg, #1C1C2E 0%, #374151 55%, #FF5200 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              Access Management
            </Typography>

            <Typography
              sx={{
                fontFamily: "Quicksand",
                fontWeight: 600,
                fontSize: { xs: 11, sm: 13 },
                color: "#9CA3AF",
              }}
            >
              {totalPeople} {totalPeople === 1 ? "person" : "people"} across your organisation — change roles,
              section access, and enabled/disabled status from here
            </Typography>
          </Box>

          {/* ── Filters ── */}
          <Stack
            direction={{ xs: "column", lg: "row" }}
            alignItems={{ xs: "stretch", lg: "center" }}
            justifyContent="space-between"
            spacing={2}
            sx={{ mb: 2.5 }}
          >
            <Tabs
              value={roleFilter}
              onChange={(_, value) => setRoleFilter(value)}
              variant="scrollable"
              scrollButtons={false}
              allowScrollButtonsMobile
              sx={{
                minHeight: 0,
                alignSelf: "flex-start",
                maxWidth: "100%",
                display: "inline-flex",
                bgcolor: "#F3F4F6",
                borderRadius: "12px",
                p: 0.5,
                "& .MuiTabs-indicator": { display: "none" },
                "& .MuiTabs-flexContainer": { gap: 0.5 },
              }}
            >
              {ROLE_TABS.map((tab) => {
                const selected = roleFilter === tab.value;
                return (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    disableRipple
                    label={`${tab.label} (${tab.value === "all" ? totalPeople : roleCounts[tab.value] || 0})`}
                    sx={{
                      textTransform: "none",
                      fontFamily: "Quicksand",
                      fontWeight: 700,
                      fontSize: 13,
                      borderRadius: "9px",
                      minHeight: 36,
                      px: 2.5,
                      transition: "all 0.2s ease",
                      color: selected ? "#fff" : "#6B7280",
                      background: selected ? "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)" : "transparent",
                      boxShadow: selected ? "0 2px 8px rgba(255,82,0,0.3)" : "none",
                      "&.Mui-selected": { color: "#fff" },
                    }}
                  />
                );
              })}
            </Tabs>

            {/* A plain flex Box, not a Stack: this project is on MUI 5.8, where Stack's
                `useFlexGap` does not exist yet and `spacing` is implemented as a margin on
                every sibling but the first. On a wrapping row that means no gap at all
                between rows, and a second item pushed onto its own line by the margin — which
                is what was stacking these buttons edge to edge on a phone. `gap` is the
                thing that actually works in both axes. */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.5,
                width: { xs: "100%", lg: "auto" },
              }}
            >
              {/* Enabled/disabled filter. A dropdown rather than another row of pills —
                  the filter row already carries the role tabs, and three more buttons was
                  squeezing them. Counts come from the backend's statusCounts, measured
                  within the current role/search/dormant filters. */}
              <Select
                size="small"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                renderValue={(value) => {
                  const option = STATUS_FILTERS.find((o) => o.value === value) || STATUS_FILTERS[0];
                  const Icon = option.icon;
                  return (
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Icon sx={{ fontSize: 15, color: option.color }} />
                      <Box component="span">
                        {option.label} ({statusCounts[option.value] || 0})
                      </Box>
                    </Stack>
                  );
                }}
                sx={{
                  flex: { xs: "1 1 calc(50% - 6px)", lg: "0 0 auto" },
                  // 0 on a phone so the flex basis is what decides the width; the 150px
                  // only applies once it sits on the desktop filter row.
                  minWidth: { xs: 0, lg: 150 },
                  height: 36,
                  bgcolor: "#fff",
                  borderRadius: "10px",
                  fontFamily: "Quicksand",
                  fontWeight: 700,
                  fontSize: 12.5,
                  // An applied filter gets its own tint, so it can't be left on by accident
                  // and mistaken for the full roster.
                  color: statusFilter === "all" ? "#6B7280" : "#1C1C2E",
                  "& fieldset": {
                    borderColor: statusFilter === "all" ? "rgba(0,0,0,0.12)" : "rgba(255,82,0,0.35)",
                  },
                  "&:hover fieldset": { borderColor: "rgba(0,0,0,0.24)" },
                  "&.Mui-focused fieldset": { borderColor: "#FF5200" },
                  "& .MuiSelect-icon": { color: "#9CA3AF" },
                }}
                MenuProps={{ PaperProps: { sx: { ...PAGINATION_MENU_SX, mt: 0.5, minWidth: 180 } } }}
              >
                {STATUS_FILTERS.map((option) => {
                  const Icon = option.icon;
                  return (
                    <MenuItem key={option.value} value={option.value}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Icon sx={{ fontSize: 16, color: option.color }} />
                        <Box component="span">
                          {option.label} ({statusCounts[option.value] || 0})
                        </Box>
                      </Stack>
                    </MenuItem>
                  );
                })}
              </Select>

              <Chip
                icon={<HistoryToggleOffIcon sx={{ fontSize: 15 }} />}
                label={`Inactive ${dormantDays}d+ (${dormantCount})`}
                onClick={() => setDormantOnly((prev) => !prev)}
                variant={dormantOnly ? "filled" : "outlined"}
                sx={{
                  flex: { xs: "1 1 calc(50% - 6px)", lg: "0 0 auto" },
                  minWidth: 0,
                  height: 36,
                  fontFamily: "Quicksand",
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: "pointer",
                  bgcolor: dormantOnly ? "rgba(227,76,20,0.12)" : "#fff",
                  color: dormantOnly ? "#E34C14" : "#6B7280",
                  borderColor: dormantOnly ? "rgba(227,76,20,0.35)" : "rgba(0,0,0,0.12)",
                  "& .MuiChip-icon": { color: "inherit" },
                }}
              />

              <Button
                size="small"
                variant="contained"
                startIcon={<PersonAddAltOutlinedIcon sx={{ fontSize: 16 }} />}
                onClick={() => setNewUserOpen(true)}
                sx={{
                  ...PRIMARY_BTN_SX,
                  flex: { xs: "1 1 calc(50% - 6px)", lg: "0 0 auto" },
                  minWidth: 0,
                  minHeight: 36,
                  whiteSpace: "nowrap",
                }}
              >
                New user
              </Button>

              <Button
                size="small"
                variant="contained"
                startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                onClick={handleExport}
                disabled={exporting || !users.length}
                sx={{
                  ...PRIMARY_BTN_SX,
                  flex: { xs: "1 1 calc(50% - 6px)", lg: "0 0 auto" },
                  minWidth: 0,
                  minHeight: 36,
                  whiteSpace: "nowrap",
                }}
              >
                {exporting ? "Exporting…" : "Export"}
              </Button>

            <TextField
              size="small"
              placeholder="Search by name or email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                width: { xs: "100%", lg: 300 },
                flexBasis: { xs: "100%", lg: "auto" },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  bgcolor: "#fff",
                  fontFamily: "Quicksand",
                  fontWeight: 600,
                  fontSize: 13,
                  "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "#9CA3AF" }} />
                  </InputAdornment>
                ),
                endAdornment: search && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch("")}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            </Box>
          </Stack>

          {/* ── Table ── */}
          {selectionCount > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 1.5,
                mb: 1.5,
                px: 2,
                py: 1.25,
                borderRadius: 2.5,
                bgcolor: "rgba(255,82,0,0.06)",
                border: "1px solid rgba(255,82,0,0.20)",
              }}
            >
              <Typography sx={{ fontFamily: "Quicksand", fontWeight: 800, fontSize: 13, color: "#E34C14" }}>
                {selectionCount} selected
              </Typography>

              {/* Without this, "disable everyone dormant" would mean ticking 20 rows a
                  page, six pages deep. */}
              {!selectAllMatching && allOnPageSelected && total > pageIds.length && (
                <Button
                  size="small"
                  onClick={() => setSelectAllMatching(true)}
                  sx={{ ...SECONDARY_BTN_SX, color: "#E34C14", textDecoration: "underline" }}
                >
                  Select all {total} matching this filter
                </Button>
              )}

              <Box sx={{ flexGrow: 1, display: { xs: "none", sm: "block" } }} />

              <Button
                size="small"
                variant="outlined"
                disabled={bulkStatus.isLoading}
                onClick={() => handleBulkStatus(true)}
                sx={{
                  textTransform: "none",
                  fontFamily: "Quicksand",
                  fontWeight: 700,
                  borderRadius: "10px",
                  color: "#09AC31",
                  borderColor: "rgba(9,172,49,0.4)",
                }}
              >
                Enable
              </Button>

              <Button
                size="small"
                variant="contained"
                disabled={bulkStatus.isLoading}
                onClick={() => handleBulkStatus(false)}
                sx={{
                  textTransform: "none",
                  fontFamily: "Quicksand",
                  fontWeight: 700,
                  borderRadius: "10px",
                  boxShadow: "none",
                  bgcolor: "#E34C14",
                  "&:hover": { bgcolor: "#c93f10" },
                }}
              >
                {bulkStatus.isLoading ? "Working…" : "Disable"}
              </Button>

              <Button size="small" onClick={clearSelection} sx={SECONDARY_BTN_SX}>
                Clear
              </Button>
            </Box>
          )}

          <Paper
            elevation={0}
            sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden", bgcolor: "#fff" }}
          >
            {/* No inner scroll cap on the card list: the cards only render below `lg`, and a
                scroll area nested inside the page's own scroll is miserable on a touch
                screen — the page scrolls, the list just gets as tall as it needs. */}
            {isCompact ? (
              <Box sx={{ p: { xs: 1, sm: 1.5 } }}>
                {/* The table's header checkbox has no home in a card list, so page-level
                    selection gets its own row here. */}
                {users.length > 0 && (
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={allOnPageSelected}
                      indeterminate={someOnPageSelected}
                      onChange={togglePage}
                      sx={{ "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#FF5200" } }}
                    />
                    <Typography sx={{ fontFamily: "Quicksand", fontWeight: 700, fontSize: 12, color: "#6B7280" }}>
                      Select all on this page
                    </Typography>
                  </Stack>
                )}

                {users.length > 0 && (
                  <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                    <Typography sx={{ fontFamily: "Quicksand", fontWeight: 700, fontSize: 11, color: "#9CA3AF" }}>
                      Sort:
                    </Typography>
                    {SORT_OPTIONS.map((opt) => {
                      const active = sort.by === opt.by;
                      return (
                        <Chip
                          key={opt.by}
                          size="small"
                          label={active ? `${opt.label} ${sort.dir === "desc" ? "↓" : "↑"}` : opt.label}
                          onClick={() => toggleSort(opt.by)}
                          variant={active ? "filled" : "outlined"}
                          sx={{
                            fontFamily: "Quicksand",
                            fontWeight: 700,
                            fontSize: 11,
                            cursor: "pointer",
                            bgcolor: active ? "rgba(255,82,0,0.12)" : "#fff",
                            color: active ? "#FF5200" : "#6B7280",
                            borderColor: active ? "rgba(255,82,0,0.35)" : "rgba(0,0,0,0.12)",
                          }}
                        />
                      );
                    })}
                  </Stack>
                )}

                {showSkeleton && <CardsSkeleton />}

                {isError && (
                  <Typography
                    align="center"
                    sx={{ fontFamily: "Quicksand", fontWeight: 700, fontSize: 13, color: "#E34C14", py: 5 }}
                  >
                    Could not load users
                  </Typography>
                )}

                {!showSkeleton && !isError && users.length === 0 && (
                  <Typography
                    align="center"
                    sx={{ fontFamily: "Quicksand", fontWeight: 700, fontSize: 13, color: "#9CA3AF", py: 5 }}
                  >
                    {emptyMessage}
                  </Typography>
                )}

                {!showSkeleton && (
                  <Stack spacing={1.25} sx={{ opacity: isFetching ? 0.6 : 1, transition: "opacity 0.12s ease" }}>
                    {users.map((user, index) => (
                      <UserCard
                        key={user.id}
                        user={user}
                        index={page * rowsPerPage + index + 1}
                        selected={selectAllMatching || selected.includes(user.id)}
                        onToggle={() => toggleRow(user.id)}
                        onOpen={() => setActiveUser(user)}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: 600, overflowX: "auto", overflowY: "auto" }}>
                <Table stickyHeader sx={{ minWidth: TABLE_MIN_WIDTH, tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...HEAD_SX, width: COL_WIDTH.select }} padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={allOnPageSelected}
                          indeterminate={someOnPageSelected}
                          onChange={togglePage}
                          sx={{ "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#FF5200" } }}
                        />
                      </TableCell>
                      <TableCell sx={{ ...HEAD_SX, width: COL_WIDTH.serial }} align="center">
                        #
                      </TableCell>
                      <TableCell sx={{ ...HEAD_SX, width: COL_WIDTH.user }}>
                        <TableSortLabel
                          active={sort.by === "name"}
                          direction={sort.by === "name" ? sort.dir : "desc"}
                          onClick={() => toggleSort("name")}
                          sx={SORT_LABEL_SX}
                        >
                          User
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ ...HEAD_SX, width: COL_WIDTH.role }}>Role</TableCell>
                      <TableCell sx={{ ...HEAD_SX, width: COL_WIDTH.balance }}>
                        <TableSortLabel
                          active={sort.by === "balance"}
                          direction={sort.by === "balance" ? sort.dir : "desc"}
                          onClick={() => toggleSort("balance")}
                          sx={SORT_LABEL_SX}
                        >
                          Balance
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ ...HEAD_SX, width: COL_WIDTH.managedBy }}>Managed by</TableCell>
                      <TableCell sx={{ ...HEAD_SX, width: COL_WIDTH.manages }}>Manages</TableCell>
                      <TableCell sx={{ ...HEAD_SX, width: COL_WIDTH.status }}>Status</TableCell>
                      <TableCell sx={{ ...HEAD_SX, width: COL_WIDTH.lastLogin }}>
                        <TableSortLabel
                          active={sort.by === "lastLogin"}
                          direction={sort.by === "lastLogin" ? sort.dir : "desc"}
                          onClick={() => toggleSort("lastLogin")}
                          sx={SORT_LABEL_SX}
                        >
                          Last login
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ ...HEAD_SX, width: COL_WIDTH.forceLogout, textAlign: "center" }}>
                        Force logout
                      </TableCell>
                      <TableCell sx={{ ...HEAD_SX, ...LAST_CELL_SX, width: COL_WIDTH.actions, pr: 2 }} align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {showSkeleton && <TableRowsSkeleton />}

                    {isError && (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ ...CELL_SX, py: 5, color: "#E34C14" }}>
                          Could not load users
                        </TableCell>
                      </TableRow>
                    )}

                    {!showSkeleton && !isError && users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={11} align="center" sx={{ ...CELL_SX, py: 5, color: "#9CA3AF" }}>
                          {emptyMessage}
                        </TableCell>
                      </TableRow>
                    )}

                    {!showSkeleton &&
                      users.map((user, index) => (
                        <TableRow
                          key={user.id}
                          onClick={() => setActiveUser(user)}
                          selected={selectAllMatching || selected.includes(user.id)}
                          sx={{
                            cursor: "pointer",
                            opacity: isFetching ? 0.6 : 1,
                            "&.Mui-selected, &.Mui-selected:hover": { bgcolor: "rgba(255,82,0,0.05)" },
                            transition: "background-color 0.15s ease, opacity 0.12s ease",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                          }}
                        >
                          <TableCell sx={CELL_SX} padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              size="small"
                              checked={selectAllMatching || selected.includes(user.id)}
                              onChange={() => toggleRow(user.id)}
                              sx={{ "&.Mui-checked": { color: "#FF5200" } }}
                            />
                          </TableCell>
                          <TableCell sx={{ ...CELL_SX, color: "#9CA3AF", fontSize: 12 }} align="center">
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                          <TableCell sx={CELL_SX}>
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                              <Avatar
                                src={avatarSrc(user.image)}
                                // Google profile URLs 403 when a referrer is sent, which is
                                // why these were all falling back to initials.
                                imgProps={{ referrerPolicy: "no-referrer" }}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  fontSize: 12,
                                  fontWeight: 800,
                                  fontFamily: "Quicksand",
                                  bgcolor: stringToColor(user.fullName),
                                }}
                              >
                                {initialsOf(user.fullName)}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{
                                    fontFamily: "Quicksand",
                                    fontWeight: 700,
                                    fontSize: 13,
                                    color: "#1C1C2E",
                                    lineHeight: 1.25,
                                  }}
                                  noWrap
                                >
                                  {user.fullName}
                                </Typography>
                                <Typography
                                  sx={{ fontFamily: "Quicksand", fontWeight: 600, fontSize: 11, color: "#9CA3AF" }}
                                  noWrap
                                >
                                  {user.email}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>
                          <TableCell sx={CELL_SX}>
                            <RoleChip role={user.role} />
                          </TableCell>
                          <TableCell sx={CELL_SX}>
                            <BalanceCell user={user} />
                          </TableCell>
                          <TableCell sx={CELL_SX}>
                            <ManagedByCell user={user} />
                          </TableCell>
                          <TableCell sx={CELL_SX}>
                            <ManagesCell user={user} />
                          </TableCell>
                          <TableCell sx={CELL_SX} onClick={(e) => e.stopPropagation()}>
                            <StatusCell user={user} />
                          </TableCell>
                          <TableCell sx={{ ...CELL_SX, color: "#9CA3AF", fontSize: 12 }}>
                            {formatLastLogin(user.lastLogin)}
                          </TableCell>
                          <TableCell sx={{ ...CELL_SX, textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                            <ForceLogoutCell user={user} />
                          </TableCell>
                          <TableCell sx={{ ...CELL_SX, ...LAST_CELL_SX, pr: 2 }} align="right" onClick={(e) => e.stopPropagation()}>
                            <Tooltip title="Manage access">
                              <IconButton size="small" onClick={() => setActiveUser(user)}>
                                <ChevronRightIcon fontSize="small" sx={{ color: "#9CA3AF" }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0); // page 5 of 20/page would land past the end at 100/page
              }}
              labelRowsPerPage="Rows"
              sx={PAGINATION_SX}
              // MUI 5.8.7 has no `slotProps` on TablePagination — SelectProps is the
              // supported way in, same trap as Dialog/Popover earlier in this feature.
              SelectProps={{ MenuProps: { PaperProps: { sx: PAGINATION_MENU_SX } } }}
            />
          </Paper>
        </Container>
      </ThemeProvider>

      {activeUser && (
        <ManageAccessDialog user={activeUser} allUsers={allUsers} onClose={() => setActiveUser(null)} />
      )}

      <NewUserDrawer open={newUserOpen} allUsers={allUsers} onClose={() => setNewUserOpen(false)} />
    </Box>
  );
}
